import { useState, useEffect } from "react";
import { BarChart3, Clock, Users, TrendingUp, FileDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { de, enUS, pt } from "date-fns/locale";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StatisticsPage({ onLogout }) {
  const { lang, t } = useLanguage();
  const dateLocale = lang === "pt" ? pt : lang === "en" ? enUS : de;
  const [shifts, setShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  useEffect(() => {
    if (workers.length > 0 && shiftTypes.length > 0) {
      calculateStatistics();
    } else if (!loading && workers.length === 0) {
      // Set empty statistics when no workers exist
      setStatistics({
        workerStats: [],
        totalShifts: 0,
        totalAbsences: 0,
        totalHours: 0,
        averageHoursPerWorker: 0
      });
    }
  }, [shifts, absences, workers, shiftTypes, loading]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [shiftsRes, absencesRes, workersRes, shiftTypesRes] = await Promise.all([
        axios.get(`${API}/shifts?month=${selectedMonth}`, { headers }),
        axios.get(`${API}/absences?month=${selectedMonth}`, { headers }),
        axios.get(`${API}/workers`, { headers }),
        axios.get(`${API}/shift-types`, { headers })
      ]);

      setShifts(shiftsRes.data);
      setAbsences(absencesRes.data);
      setWorkers(workersRes.data);
      setShiftTypes(shiftTypesRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const calculateHours = (startTime, endTime) => {
    if (startTime === '--:--' || endTime === '--:--') return 0;
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let hours = endHour - startHour;
    let minutes = endMin - startMin;
    
    if (hours < 0) hours += 24;
    
    return hours + (minutes / 60);
  };

  const calculateStatistics = () => {
    const workerStats = workers.map(worker => {
      const workerShifts = shifts.filter(s => s.worker_id === worker.id);
      const workerAbsences = absences.filter(a => a.worker_id === worker.id);
      
      let totalHours = 0;
      const shiftTypeCounts = {};
      
      workerShifts.forEach(shift => {
        let hours = 0;
        
        if (shift.custom_start_time && shift.custom_end_time) {
          hours = calculateHours(shift.custom_start_time, shift.custom_end_time);
        } else {
          const shiftType = shiftTypes.find(st => st.id === shift.shift_type_id);
          if (shiftType) {
            hours = calculateHours(shiftType.start_time, shiftType.end_time);
            shiftTypeCounts[shiftType.name] = (shiftTypeCounts[shiftType.name] || 0) + 1;
          }
        }
        
        totalHours += hours;
      });
      
      const absenceCounts = {};
      workerAbsences.forEach(absence => {
        absenceCounts[absence.type] = (absenceCounts[absence.type] || 0) + 1;
      });
      
      return {
        worker,
        totalShifts: workerShifts.length,
        totalHours: Math.round(totalHours * 10) / 10,
        shiftTypeCounts,
        absenceCounts,
        totalAbsences: workerAbsences.length,
        averageHoursPerShift: workerShifts.length > 0 ? Math.round((totalHours / workerShifts.length) * 10) / 10 : 0
      };
    });

    const totalShifts = shifts.length;
    const totalAbsences = absences.length;
    const totalHours = workerStats.reduce((sum, stat) => sum + stat.totalHours, 0);
    const averageHoursPerWorker = workers.length > 0 ? Math.round((totalHours / workers.length) * 10) / 10 : 0;

    setStatistics({
      workerStats,
      totalShifts,
      totalAbsences,
      totalHours: Math.round(totalHours * 10) / 10,
      averageHoursPerWorker
    });
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    for (let i = -6; i <= 6; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const value = format(date, 'yyyy-MM');
      const label = format(date, 'MMMM yyyy', { locale: de });
      options.push({ value, label });
    }
    return options;
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/shifts-table-pdf?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `arbeitsstunden_bericht_${selectedMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF-Bericht heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim PDF-Download");
    }
  };

  if (loading || !statistics) {
    return (
      <Layout onLogout={onLogout}>
        <div className="flex items-center justify-center h-full">
          <p className="text-slate-600">Laden...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6" data-testid="statistics-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Arbeitsstatistiken
            </h1>
            <p className="text-slate-600 mt-1">Übersicht der geleisteten Arbeitsstunden</p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48" data-testid="month-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {generateMonthOptions().map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={downloadPDF} data-testid="download-pdf-button">
              <FileDown className="w-4 h-4 mr-2" />
              PDF-Bericht
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="total-shifts-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">{t("total_shifts")}</p>
                <p className="text-2xl font-bold text-slate-900">{statistics.totalShifts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="total-hours-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">{t("total")} {t("hours")}</p>
                <p className="text-2xl font-bold text-slate-900">{statistics.totalHours}h</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="total-absences-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">{t("total_absences")}</p>
                <p className="text-2xl font-bold text-slate-900">{statistics.totalAbsences}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="avg-hours-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Ø Stunden/Mitarbeiter</p>
                <p className="text-2xl font-bold text-slate-900">{statistics.averageHoursPerWorker}h</p>
              </div>
            </div>
          </div>
        </div>

        {/* Worker Statistics */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="p-5 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mitarbeiter-Details
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">{t("worker_label")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">{t("shifts_count")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">{t("total")} {t("hours")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Ø Stunden/Schicht</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Abwesenheiten</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Schichttypen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {statistics.workerStats.map((stat, idx) => (
                  <tr key={stat.worker.id} className="hover:bg-slate-50" data-testid={`worker-stat-row-${idx}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-slate-900">{stat.worker.name}</div>
                        {stat.worker.role && (
                          <div className="text-sm text-slate-500">{stat.worker.role}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-700">
                      {stat.totalShifts}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {stat.totalHours}h
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-700">
                      {stat.averageHoursPerShift}h
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {stat.totalAbsences > 0 ? (
                          <>
                            {Object.entries(stat.absenceCounts).map(([type, count]) => (
                              <span key={type} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                type === 'Urlaub' ? 'bg-green-100 text-green-800' :
                                type === 'Krank' ? 'bg-red-100 text-red-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {type}: {count}
                              </span>
                            ))}
                          </>
                        ) : (
                          <span className="text-slate-400 text-xs">Keine</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(stat.shiftTypeCounts).map(([type, count]) => (
                          <span key={type} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {type}: {count}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}